/*
    NbaTeamLineups Component
    This component is used to display the team lineups data for a given game.

    it will display:
        - Starters  (Starting = true), with Confirmed Status for each team (either confirmed or projected)
        - Bench (Starting = false)

        Stats for each player in the lineup - including:
            - PointsPerGame, MinutesPerGame, EffectiveFieldGoalsPercentage 

    This component uses a unified comparative layout where both teams' stats are displayed together 
    in each section for easier comparison, with clear team identification. When on Mobile there should be a switch to toggle between the two teams..

    
    Props: 
        - gameLineups: dict with HomeTeamID, AwayTeamID, HomeLineup (Array), AwayLineup (Array) 
        - teamStats: object keyed by TeamID containing arrays of player stat objects
        - homeTeamLabel: home team label
        - awayTeamLabel: away team label
*/

import React, { useState } from 'react';

const NbaTeamLineups = ({ gameLineups, teamStats, homeTeamLabel = null, awayTeamLabel = null, onPlayerClick }) => {
    const [selectedTeam, setSelectedTeam] = useState('home'); // 'both', 'home', 'away'

    // Extract lineup data
    const homeTeamID = gameLineups?.HomeTeamID;
    const awayTeamID = gameLineups?.AwayTeamID;
    const homeLineup = gameLineups?.HomeLineup || [];
    const awayLineup = gameLineups?.AwayLineup || [];

    // Separate starters and bench players
    const homeStarters = homeLineup.filter(player => player.Starting === true);
    const homeBench = homeLineup.filter(player => player.Starting === false);
    const awayStarters = awayLineup.filter(player => player.Starting === true);
    const awayBench = awayLineup.filter(player => player.Starting === false);
    const awayTeamConfirmed = awayLineup.some(player => player.Confirmed === true) ? 'Confirmed' : 'Projected';
    const homeTeamConfirmed = homeLineup.some(player => player.Confirmed === true) ? 'Confirmed' : 'Projected';

    // Helper function to get player stats
    const getPlayerStats = (teamID, playerID) => {
        if (!teamStats || !teamStats[teamID]) return null;
        return teamStats[teamID].find(stat => stat.PlayerID === playerID);
    };

    // Helper function to format player stats
    const formatPlayerStats = (stats) => {
        if (!stats) return { ppg: 'N/A', mpg: 'N/A', efg: 'N/A' };
        
        const ppg = stats.PointsPerGame?.toFixed(1) || 'N/A';
        const mpg = stats.MinutesPerGame?.toFixed(1) || 'N/A';
        const efg = stats.EffectiveFieldGoalsPercentage?.toFixed(3) || 'N/A';
        
        return { ppg, mpg, efg };
    };

    // Render player row
    const renderPlayerRow = (player, teamID) => {
        if (!player) return null;

        const stats = getPlayerStats(teamID, player.PlayerID);
        const { ppg, mpg, efg } = formatPlayerStats(stats);

        return (
            <div 
                key={player.PlayerID}
                className="px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => onPlayerClick?.(player, teamID)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-900 dark:text-gray-100">
                                    {player.FirstName} {player.LastName}
                                </span>
                            </div>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                {player.Position}
                            </span>
                        </div>
                    </div>
                    <div className="flex gap-2 text-xs">
                        <div className="text-right min-w-[3rem]">
                            <div className="text-gray-500 dark:text-gray-400 text-[10px]">PPG</div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">{ppg}</div>
                        </div>
                        <div className="text-right min-w-[3rem]">
                            <div className="text-gray-500 dark:text-gray-400 text-[10px]">MPG</div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">{mpg}</div>
                        </div>
                        <div className="text-right min-w-[3rem]">
                            <div className="text-gray-500 dark:text-gray-400 text-[10px]">eFG%</div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">{efg}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Check if any lineup data exists
    const hasLineupData = homeLineup.length > 0 || awayLineup.length > 0;

    if (!hasLineupData) {
        return (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No lineup data available
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4 relative mt-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Team Lineups</h3>
            </div>
            
            {/* Starters Section */}
            {(homeStarters.length > 0 || awayStarters.length > 0) && (
                <div className="mb-6">
                    <h3 className="flex items-center justify-center text-base font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                        Starting Lineups
                    </h3>
                    
                    {/* Desktop: Comparative Layout */}
                    <div className="hidden lg:block">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="text-center">
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {awayTeamLabel} (Away)
                                </span>
                            </div>
                            <div className="text-center">
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {homeTeamLabel} (Home)
                                </span>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            {/* Away Team Starters */}
                            <div className="space-y-1">
                                {awayStarters.length > 0 ? (
                                    awayStarters.map(player => renderPlayerRow(player, awayTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Starters not available
                                    </div>
                                )}
                            </div>

                            {/* Home Team Starters */}
                            <div className="space-y-1">
                                {homeStarters.length > 0 ? (
                                    homeStarters.map(player => renderPlayerRow(player, homeTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Starters not available
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mobile: Toggle Layout */}
                    <div className="block lg:hidden">
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setSelectedTeam('away')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                    selectedTeam === 'away'
                                        ? 'bg-gray-700 dark:bg-gray-600 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                {awayTeamLabel}
                            </button>
                            <button
                                onClick={() => setSelectedTeam('home')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                    selectedTeam === 'home'
                                        ? 'bg-gray-700 dark:bg-gray-600 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                {homeTeamLabel}
                            </button>
                        </div>

                        {selectedTeam === 'away' && (
                            <div className="space-y-1">
                                {awayStarters.length > 0 ? (
                                    awayStarters.map(player => renderPlayerRow(player, awayTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Starters not available
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedTeam === 'home' && (
                            <div className="space-y-1">
                                {homeStarters.length > 0 ? (
                                    homeStarters.map(player => renderPlayerRow(player, homeTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Starters not available
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Bench Section */}
            {(homeBench.length > 0 || awayBench.length > 0) && (
                <div className="mb-6">
                    <h3 className="flex items-center justify-center text-base font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                        Bench
                    </h3>
                    
                    {/* Desktop: Comparative Layout */}
                    <div className="hidden lg:block">
                        <div className="grid grid-cols-2 gap-4">
                            {/* Away Team Bench */}
                            <div className="space-y-1">
                                {awayBench.length > 0 ? (
                                    awayBench.map(player => renderPlayerRow(player, awayTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Bench not available
                                    </div>
                                )}
                            </div>

                            {/* Home Team Bench */}
                            <div className="space-y-1">
                                {homeBench.length > 0 ? (
                                    homeBench.map(player => renderPlayerRow(player, homeTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Bench not available
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mobile: Toggle Layout */}
                    <div className="block lg:hidden">
                        {selectedTeam === 'away' && (
                            <div className="space-y-1">
                                {awayBench.length > 0 ? (
                                    awayBench.map(player => renderPlayerRow(player, awayTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Bench not available
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedTeam === 'home' && (
                            <div className="space-y-1">
                                {homeBench.length > 0 ? (
                                    homeBench.map(player => renderPlayerRow(player, homeTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Bench not available
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NbaTeamLineups;